<template>
  <KkUpperTabLayout
    :menu-list="tabList"
    :selected-tab-key="selectedTab"
    @click-menu="clickTab($event)"
  >
    <template #nav-link>
      <router-link :to="{ name: 'CompaniesInvitationForm', params: { company_uuid: currentCompanyUuid } }" class="c-button__item c-button__item--primary">{{ $t('labels.add', { value: $t('models.user.member') }) }}</router-link>
    </template>
    <div class="c-searchField">
      <input
        v-model="searchQuery"
        type="text"
        class="searchForm"
        :placeholder="$t('labels.search')"
        @keydown.enter="search"
      >
      <KkIcon name="search" />
    </div>
    <Loading v-if="loading" />
    <div v-if="!loading && selectedTab === 'MEMBER'" class="p-table">
      <div class="p-table__content">
        <table>
          <thead>
            <tr>
              <th><span>{{ $t('models.user.name') }}</span></th>
              <th><span>{{ $t('models.user.email') }}</span></th>
              <th><span>{{ $t('labels.lastAccessedAt') }}</span></th>
              <th><span>{{ $t('labels.role') }}</span></th>
              <th><span>{{ $t('labels.storeCount') }}</span></th>
              <th><span>{{ $t('labels.operation') }}</span></th>
            </tr>
            <tr class="p-table__content__space">
              <td colspan="5" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="user in users"
              :key="'u-' + user.id"
            >
              <td class="u-fwB">{{ user.attributes.name }}</td>
              <td>{{ displayEmail(user) }}</td>
              <td>{{ basicDateTimeFormat(user.attributes.accessed_at) }}</td>
              <td>{{ displayRole(user.attributes.role) }}</td>
              <td>
                <template v-if="user.attributes.readable_all_store || user.attributes.readable_store_count === 0">{{ displayReadableStoreCount(user.attributes) }}</template>
                <button v-else class="storeCount" @click="showStoreModal(user.attributes.readable_stores)">
                  <span>{{ displayReadableStoreCount(user.attributes) }}</span>
                </button>
              </td>
              <td>
                <router-link v-if="currentUser.id != Number(user.id) && editableUser(user.attributes.role)" :to="{ name: 'CompaniesMemberRoleEdit', params: { company_uuid: currentCompanyUuid, user_company_id: user.attributes.user_company_id } }" class="action-item">
                  <img :src="imgIconPencil" alt="" class="action-icon">
                  {{ $t('labels.edit') }}
                </router-link>
                <a v-if="!isLoginUserData(user) && editableUser(user.attributes.role)" class="action-item" @click="deleteUser(user.id)">
                  <img :src="imgIconCross" alt="" class="action-icon">
                  {{ $t('pages.companies.managementMembers.removeUserFromMembers') }}
                </a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <KkPagination
        :loading="loading"
        :current-page="currentPage"
        :display-number="Number(displayNumber)"
        :contents-length="users.length"
        :total-pages="usersMeta ? Number(usersMeta.totalPages) : 1"
        @onChangeDisplayNumber="changeDisplayNumber($event)"
        @goPrevPage="goPrevPage"
        @goNextPage="goNextPage"
      />
    </div>
    <div v-if="!loading && selectedTab === 'INVITING'" class="p-table">
      <div class="p-table__content">
        <table>
          <thead>
            <tr>
              <th><span>{{ $t('labels.status') }}</span></th>
              <th><span>{{ $t('models.user.email') }}</span></th>
              <th><span>{{ $t('labels.invitedAt') }}</span></th>
              <th><span>{{ $t('labels.role') }}</span></th>
              <th><span>{{ $t('labels.storeCount') }}</span></th>
              <th><span>{{ $t('labels.operation') }}</span></th>
            </tr>
            <tr class="p-table__content__space">
              <td colspan="5" />
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="invitation in invitations"
              :key="'i-' + invitation.id"
            >
              <td class="u-fwB">
                <div class="flexContainer">
                  <div class="u-colorPrimary">{{ invitationStatus(invitation.attributes.expired) }}</div>
                </div>
              </td>
              <td>{{ invitation.attributes.email }}</td>
              <td>{{ displayInvitationAt(invitation.attributes.updated_at) }}</td>
              <td>{{ displayRole(invitation.attributes.role) }}</td>
              <td>
                <template v-if="invitation.attributes.readable_all_store || invitation.attributes.readable_store_count === 0">{{ displayReadableStoreCount(invitation.attributes) }}</template>
                <button v-else class="storeCount" @click="showStoreModal(invitation.attributes.readable_stores)">
                  <span>{{ displayReadableStoreCount(invitation.attributes) }}</span>
                </button>
              </td>
              <td>
                <a class="action-item" @click="resendInvitation(invitation.id)">{{ $t('labels.resend') }}</a>
                <a class="action-item" @click="cancelInvitation(invitation.id)">{{ $t('labels.cancel') }}</a>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      <KkPagination
        v-if="!loading"
        :current-page="currentPage"
        :display-number="Number(displayNumber)"
        :contents-length="invitations.length"
        :total-pages="invitationsMeta ? Number(invitationsMeta.totalPages) : 1"
        @onChangeDisplayNumber="changeDisplayNumber($event)"
        @goPrevPage="goPrevPage"
        @goNextPage="goNextPage"
      />
    </div>
  </KkUpperTabLayout>
  <KkModal :is-visible="isShowStoreModal" @change="isShowStoreModal = false">
    <div class="content__header">
      {{ $t('pages.companies.managementMembers.listOfStores') }}
    </div>
    <div class="content__body">
      <div v-for="(storeName, index) in storeNames" :key="index" class="item">
        {{ storeName }}
      </div>
    </div>
    <div class="content__footer">
      <div class="buttonGroup">
        <div class="buttonGroup__item">
          <KkButton color="secondary" outlined :text="$t('labels.close')" @click="isShowStoreModal = false" />
        </div>
      </div>
    </div>
  </KkModal>
</template>

<script>
import {mapActions, mapState, mapGetters} from 'vuex'
import moment from 'moment'
import ImgIconCross from '../../../../images/common/icon_cross.svg'
import ImgIconPencil from '../../../../images/common/icon_pencil.svg'
import { roleConst } from '../../modules/roleConst'
import { i18n } from '../../plugins/i18n'
import Loading from '../../components/Loading'
import { KkModal, KkButton, KkIcon } from '../../components/base'
import { KkUpperTabLayout } from '../../components/projects'
import { KkPagination } from '../../components/ui'

export default {
  name: 'CompaniesManagementMembers',
  components: {
    Loading,
    KkModal,
    KkButton,
    KkUpperTabLayout,
    KkPagination,
    KkIcon
  },
  emits: ['update-title', 'update-breadcrumbs', 'update-progress'],
  data() {
    return {
      currentUser: gon.currentUser,
      isShowStoreModal: false,
      storeNames: [],
      selectedTab: 'MEMBER',
      currentPage: 1,
      searchQuery: '',
      isSearch: false
    }
  },
  computed: {
    ...mapState({
      users: state => state.companiesUsers.data,
      invitations: state => state.companiesInvitations.data,
      loading: state => state.companiesUsers.loading || state.companiesInvitations.loading,
      companiesAccessibleApi: state => state.companiesAccessibleApi.data,
    }),
    ...mapGetters({
      displayNumber: 'filters/displayNumber',
      usersMeta: 'companiesUsers/meta',
      invitationsMeta: 'companiesInvitations/meta',
    }),
    currentCompanyUuid() {
      return this.$route.params.company_uuid
    },
    imgIconPencil() {
      return ImgIconPencil
    },
    imgIconCross() {
      return ImgIconCross
    },
    currentUserRole() {
      return this.companiesAccessibleApi ? this.companiesAccessibleApi.meta.role : null
    },
    tabList() {
      return {
        'MEMBER': `${this.$t('pages.companies.managementMembers.member')}${this.usersMeta.totalCount ? `(${this.usersMeta.totalCount})` : '()'}`,
        'INVITING': `${this.$t('pages.companies.managementMembers.inviting')}${this.invitationsMeta ? `(${this.invitationsMeta.totalCount})` : '()'}`
      }
    },
    readMemberQuery() {
      const query = {
        per: this.displayNumber,
        page: this.currentPage,
      }
      if(this.searchQuery) query['query'] = this.searchQuery
      return query
    }
  },
  created() {
    this.readUsersForFilter({ company_uuid: this.currentCompanyUuid, query: this.readMemberQuery })
    this.readInvitationsForFilter({ company_uuid: this.currentCompanyUuid, query: this.readMemberQuery })
  },
  methods: {
    ...mapActions({
      readUsersForFilter: 'companiesUsers/readUsersForFilter',
      readInvitationsForFilter: 'companiesInvitations/readInvitationsForFilter',
      updateInvitations: 'companiesInvitations/updateInvitations',
      deleteInvitations: 'companiesInvitations/deleteInvitations',
      deleteUsers: 'companiesUsers/deleteUsers',
      setDisplayNumber: 'filters/setDisplayNumber',
    }),
    isLoginUserData(user) {
      return this.currentUser.id === Number(user.id)
    },
    invitationStatus(expired) {
      return expired ? this.$t('models.invitation.status.expired') : this.$t('models.invitation.status.inviting')
    },
    displayInvitationAt(datetime) {
      return moment(new Date(datetime)).format(i18n.t('format.datetime.default'))
    },
    resendInvitation(invitation_id) {
      if (confirm(this.$t('pages.companies.managementMembers.confirmToResentInvitation'))) {
        this.updateInvitations({ company_uuid: this.currentCompanyUuid, invitation_id: invitation_id })
      }
    },
    cancelInvitation(invitation_id) {
      if (!confirm(this.$t('pages.companies.managementMembers.confirmToCancelInvitation'))) return
      this.deleteInvitations({ company_uuid: this.currentCompanyUuid, invitation_id: invitation_id })
    },
    deleteUser(user_id) {
      if (confirm(this.$t('pages.companies.managementMembers.confirmToRemoveUserFromMembers'))) {
        this.deleteUsers({ company_uuid: this.currentCompanyUuid, user_id: user_id })
      }
    },
    displayEmail(user) {
      if (this.isLoginUserData(user)) {
        return user.attributes.email + '（ '+ this.$t('labels.loggedIn') + '）'
      } else {
        return user.attributes.email
      }
    },
    displayRole(role) {
      switch (role) {
      case roleConst.owner:
        return this.$t('labels.roles.owner')
      case roleConst.editor:
        return this.$t('labels.roles.editor')
      case roleConst.operator:
        return this.$t('labels.roles.operator')
      case roleConst.reader:
        return this.$t('labels.roles.reader')
      }
    },
    displayReadableStoreCount(attributes) {
      if (attributes.readable_all_store) {
        return this.$t('labels.allStores')
      } else {
        return this.$tc('labels.storesCount', attributes.readable_store_count)
      }
    },
    editableUser(targetRole) {
      return this.currentUserRole === roleConst.owner || targetRole !== roleConst.owner
    },
    basicDateTimeFormat( date ) {
      return date ? moment( date ).format(this.$t('format.datetime.default')) : ' '
    },
    showStoreModal(storeNames) {
      this.storeNames = storeNames
      this.isShowStoreModal = true
    },
    handleSubmit() {
      switch (this.selectedTab) {
      case 'MEMBER':
        this.readUsersForFilter({ company_uuid: this.currentCompanyUuid, query: this.readMemberQuery })
        break
      case 'INVITING':
        this.readInvitationsForFilter({ company_uuid: this.currentCompanyUuid, query: this.readMemberQuery })
        break
      }
    },
    clickTab(key) {
      this.resetPage()
      this.selectedTab = key
      this.searchQuery = ''
      this.isSearch = false
      this.handleSubmit()
    },
    resetPage() {
      this.currentPage = 1
    },
    goPrevPage() {
      --this.currentPage
      this.changePage()
    },
    goNextPage() {
      ++this.currentPage
      this.changePage()
    },
    changePage() {
      window.scrollTo({ top: 0 })
      this.handleSubmit()
    },
    changeDisplayNumber(e) {
      this.resetPage()
      this.setDisplayNumber(e.target.value)
      this.handleSubmit()
    },
    search(event) {
      if (event.keyCode !== 13) return
      if (this.searchQuery === '' && !this.isSearch) return
      this.isSearch = !!this.searchQuery

      this.resetPage()
      this.handleSubmit()
    },
  }
}
</script>
<style lang="scss" scoped>
@import '../../../../stylesheets/biz_spa/foundation/_var.scss';

.action-item {
  display: flex;
  align-items: center;
  margin-right: 24px;
  font-size: 12px;
  text-decoration: none;
  cursor: pointer;
  &:not(:first-child) {
    margin-top: 12px;
  }
}
.action-icon {
  width: 18px;
  margin-right: 8px;
  img {
    display: block;
    width: 100%;
  }
}
.flexContainer {
  display: flex;
}
.storeCount {
  span {
    color: #E9446A;
    font-weight: bold;
    text-decoration: underline;
  }
}
.content {
  &__header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    padding: 20px 32px;
    border-bottom: 1px solid $color-base-c;
    font-size: 16px;
    line-height: 1.5;
  }

  &__footer {
    padding: 32px;
    border-top: 1px solid $color-base-c;
  }

  &__body {
    height: 50vh;
    padding: 32px;
    overflow-y: scroll;

    .item {
      padding: 0 12px 12px;
    }
  }

  &__footer {
    display: flex;
    justify-content: flex-end;
  }
}

.buttonGroup {
  display: flex;
  flex-grow: 0;

  &__item {
    margin: 8px
  }
}
.p-table {
  td:empty {
    display: table-cell;
  }
}
.c-searchField {
  width: 220px;
  margin-left: auto;
  svg {
    width: 18px;
  }
}
</style>
